import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '../../hooks/useMediaQuery.ts';
import { Dialog } from '../Dialog/Dialog.tsx';
import { usePlacementUnder } from '../popup-placement.ts';
import { useClosedOutside } from '../use-closed-outside.ts';
import styles from './Popover.module.css';

export type PopoverProps = {
  open: boolean;
  anchor: RefObject<HTMLElement | null>;
  title: string;
  subtitle?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
};

const FOCUSABLE =
  'input:not([disabled]), select:not([disabled]), button:not([disabled]), textarea:not([disabled])';

const useEscapeToClose = (close: () => void): void => {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [close]);
};

const useFocusWithin = (
  content: RefObject<HTMLElement | null>,
  anchor: RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    const opener = anchor.current;
    content.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      opener?.focus();
    };
  }, [content, anchor]);
};

const AnchoredPanel = ({ anchor, title, subtitle, closeLabel, onClose, children, footer }: PopoverProps) => {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const placed = usePlacementUnder(anchor, true, 'end');
  useClosedOutside(panel, true, onClose, anchor);
  useEscapeToClose(onClose);
  useFocusWithin(content, anchor);
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-labelledby={titleId}
      className={styles['popover']}
      style={{ ...placed, width: undefined }}
    >
      <header className={styles['header']}>
        <div className={styles['heading']}>
          <h2 id={titleId} className={styles['title']}>
            {title}
          </h2>
          {subtitle === undefined ? null : <div className={styles['subtitle']}>{subtitle}</div>}
        </div>
        <button type="button" className={styles['close']} onClick={onClose} aria-label={closeLabel}>
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      <div ref={content} className={styles['body']}>
        {children}
      </div>
      {footer === undefined ? null : <footer className={styles['footer']}>{footer}</footer>}
    </div>,
    document.body,
  );
};

export const Popover = (props: PopoverProps) => {
  const isTablet = useBreakpoint('md');
  if (!props.open) return null;
  if (isTablet) return <AnchoredPanel {...props} />;
  return (
    <Dialog
      open
      variant="side"
      title={props.title}
      subtitle={props.subtitle}
      closeLabel={props.closeLabel}
      onClose={props.onClose}
      footer={props.footer}
    >
      {props.children}
    </Dialog>
  );
};
